import axios from "axios"

declare global {
  interface Window {
    initDragSort: () => void
  }
}

const DRAG_SORT_ATTR = "data-drag-sort"
const DRAG_ITEM_ATTR = "data-drag-sort-item"
const DRAG_HANDLE_ATTR = "data-drag-sort-handle"
const DRAG_KEY_ATTR = "data-drag-sort-key"
const DRAG_ENDPOINT_ATTR = "data-drag-sort-endpoint"

let draggedEl: HTMLElement | null = null

let pendingDragItem: HTMLElement | null = null

const getDragItems = (container: HTMLElement): HTMLElement[] =>
  Array.from(container.querySelectorAll(`[${DRAG_ITEM_ATTR}]`)) as HTMLElement[]

const postOrder = async (container: HTMLElement) => {
  const endpoint = container.getAttribute(DRAG_ENDPOINT_ATTR)
  if (!endpoint) {
    return
  }

  const items = getDragItems(container)
  const hints = items.map((el) => {
    const key = el.getAttribute(DRAG_KEY_ATTR)
    if (!key) {
      return null
    }

    const title = (el.querySelector(`[name="${key}-title"]`) as HTMLInputElement | null)?.value ?? ""
    const prompt = (el.querySelector(`[name="${key}-prompt"]`) as HTMLTextAreaElement | null)?.value ?? ""
    return { title, prompt }
  }).filter(i => !!i)

  await axios.post(endpoint, { hints }, {
    headers: {
      "Content-Type": "application/json"
    }
  })

  document.body.dispatchEvent(new CustomEvent("showSuccessToast", { detail: { value: "Hints order saved successfully" } }))
}

const initContainer = (container: HTMLElement) => {
  if (container.getAttribute("data-drag-sort-initialized")) {
    return  
  }

  container.setAttribute("data-drag-sort-initialized", "true")

  container.addEventListener("mousedown", (e) => {
    const handle = (e.target as HTMLElement).closest(`[${DRAG_HANDLE_ATTR}]`)
    pendingDragItem = handle
      ? (handle.closest(`[${DRAG_ITEM_ATTR}]`))
      : null
  })

  container.addEventListener("dragstart", (e) => {
    if (!pendingDragItem) {
      e.preventDefault()
      return
    }

    draggedEl = pendingDragItem
    pendingDragItem = null

    const clone = draggedEl.cloneNode(true) as HTMLElement
    const rect = draggedEl.getBoundingClientRect()
    clone.style.cssText = `width:${rect.width}px;`

    document.body.appendChild(clone)
    e.dataTransfer?.setDragImage(clone, e.clientX - rect.left, e.clientY - rect.top)
    
    requestAnimationFrame(() => {
      document.body.removeChild(clone)
      draggedEl?.classList.add("drag-sort-item--dragging")
    })
  })

  container.addEventListener("dragend", () => {
    if (!draggedEl) {
      return
    }

    draggedEl.classList.remove("drag-sort-item--dragging")
    draggedEl = null
    postOrder(container)
  })

  container.addEventListener("dragover", (e) => {
    e.preventDefault()
    if (!draggedEl) {
      return
    }

    const target = (e.target as HTMLElement).closest(`[${DRAG_ITEM_ATTR}]`) as HTMLElement | null
    if (!target || target === draggedEl) {
      return
    }

    const items = getDragItems(container)
    const draggedIndex = items.indexOf(draggedEl)
    const targetIndex = items.indexOf(target)

    if (draggedIndex < targetIndex) {
      container.insertBefore(draggedEl, target.nextSibling)
    } else {
      container.insertBefore(draggedEl, target)
    }
  })
}

window.initDragSort = () => {
  const containers = document.querySelectorAll(`[${DRAG_SORT_ATTR}]`) as NodeListOf<HTMLElement>
  containers.forEach(initContainer)
}
